<template>
  <div v-redirect-if-no-permission:VIEW_CAPTAIN_LEDGER class="fin-page">
    <finance-section-nav />
    <v-dialog v-model="settlementDialog" persistent max-width="500px">
      <v-card style="padding: 10px">
        <v-card-title>
          <span class="headline">{{ $t("finance.payments.addSettlement") }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeSettlementDialog">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-form>
            <v-text-field
              v-model.number="settlementForm.amount"
              :label="$t('finance.common.amount')"
              type="number"
              :placeholder="$t('finance.common.amount')"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn flat color="primary" style="width: 50%" :loading="settling" @click="submitSettlement('paid_to_driver')">
            {{ $t("finance.payments.paidToDriver") }}
          </v-btn>
          <v-btn flat color="primary" style="width: 50%" :loading="settling" @click="submitSettlement('received_from_driver')">
            {{ $t("finance.payments.receivedFromDriver") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-tabs v-model="activeTab" color="#FAFAFA" slider-color="primary" fixed-tabs>
      <v-tab :key="1" ripple active-class="tab">{{ $t("finance.payments.pending") }}</v-tab>
      <v-tab :key="2" ripple active-class="tab">{{ $t("finance.payments.done") }}</v-tab>

      <v-tab-item>
        <div class="desktop-view">
          <div class="fin-header fin-toolbar pay-header">
            <span class="fin-title">{{ $t("finance.payments.pendingPayments") }}</span>
            <v-spacer></v-spacer>
            <div class="fin-filters">
              <v-text-field
                v-model="search.pending"
                class="fin-field fin-field--wide"
                append-icon="search"
                :label="$t('finance.common.search')"
                single-line
                hide-details
              ></v-text-field>
            </div>
          </div>

          <v-data-table
            :headers="headers"
            :items="filteredPendingItems"
            :rows-per-page-items="rowsPerPageItems"
            :loading="loading.pending"
            class="fin-dt elevation-1 hidden-sm-and-down"
          >
            <template v-slot:items="props">
              <tr>
                <td class="text-xs-start">{{ props.item.id }}</td>
                <td class="text-xs-start">{{ props.item.first_name }}</td>
                <td class="text-xs-start">{{ props.item.last_name }}</td>
                <td class="text-xs-start">{{ props.item.phone }}</td>
                <td class="text-xs-start">{{ props.item.email || '-' }}</td>
                <td class="text-xs-start">{{ props.item.type }}</td>
                <td class="fin-num">{{ formatBalance(props.item.balance) }}</td>
                <td class="text-xs-center">
                  <v-btn icon small @click="openLedger(props.item.id)">
                    <v-icon>receipt_long</v-icon>
                  </v-btn>
                  <v-btn icon small @click="goToCaptain(props.item.id)">
                    <v-icon>account_circle</v-icon>
                  </v-btn>
                  <v-btn v-if="hasPermission('SETTLE_CAPTAIN_BALANCE')" icon small @click="openSettlementDialog(props.item)">
                    <v-icon>monetization_on</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>

          <div class="mobile-view hidden-md-and-up">
            <div v-for="item in filteredPendingItems" :key="`pending-${item.id}`" class="mb-3 payment-card v-card v-sheet theme--light elevation-2">
              <div class="v-card__text">
                <div class="card-row"><span class="card-label">#:</span><span class="card-value">{{ item.id }}</span></div>
                <div class="card-row"><span class="card-label">{{ $t("finance.common.phone") }}:</span><span class="card-value">{{ item.phone }}</span></div>
                <div class="card-row"><span class="card-label">{{ $t("finance.common.email") }}:</span><span class="card-value">{{ item.email || '-' }}</span></div>
                <div class="card-row"><span class="card-label">{{ $t("finance.common.name") }}:</span><span class="card-value">{{ item.first_name }} {{ item.last_name }}</span></div>
                <div class="card-row"><span class="card-label">{{ $t("finance.common.type") }}:</span><span class="card-value">{{ item.type }}</span></div>
                <div class="card-row"><span class="card-label">{{ $t("finance.common.balance") }}:</span><span class="card-value balance positive">{{ formatBalance(item.balance) }}</span></div>
              </div>
              <div class="v-card__actions card-actions">
                <v-btn icon small @click="openLedger(item.id)"><v-icon>receipt_long</v-icon></v-btn>
                <v-btn icon small @click="goToCaptain(item.id)"><v-icon>account_circle</v-icon></v-btn>
                <v-btn v-if="hasPermission('SETTLE_CAPTAIN_BALANCE')" icon small @click="openSettlementDialog(item)"><v-icon>monetization_on</v-icon></v-btn>
              </div>
            </div>
          </div>
        </div>
      </v-tab-item>

      <v-tab-item>
        <div class="desktop-view">
          <div class="fin-header fin-toolbar pay-header">
            <span class="fin-title">{{ $t("finance.payments.donePayments") }}</span>
            <v-spacer></v-spacer>
            <div class="fin-filters">
              <v-text-field
                v-model="search.done"
                class="fin-field fin-field--wide"
                append-icon="search"
                :label="$t('finance.common.search')"
                single-line
                hide-details
              ></v-text-field>
            </div>
          </div>

          <v-data-table
            :headers="headers"
            :items="filteredDoneItems"
            :rows-per-page-items="rowsPerPageItems"
            :loading="loading.done"
            class="fin-dt elevation-1 hidden-sm-and-down"
          >
            <template v-slot:items="props">
              <tr>
                <td class="text-xs-start">{{ props.item.id }}</td>
                <td class="text-xs-start">{{ props.item.first_name }}</td>
                <td class="text-xs-start">{{ props.item.last_name }}</td>
                <td class="text-xs-start">{{ props.item.phone }}</td>
                <td class="text-xs-start">{{ props.item.email || '-' }}</td>
                <td class="text-xs-start">{{ props.item.type }}</td>
                <td class="fin-num">{{ formatBalance(props.item.balance) }}</td>
                <td class="text-xs-center">
                  <v-btn icon small @click="openLedger(props.item.id)">
                    <v-icon>receipt_long</v-icon>
                  </v-btn>
                  <v-btn icon small @click="goToCaptain(props.item.id)">
                    <v-icon>account_circle</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>

          <div class="mobile-view hidden-md-and-up">
            <div v-for="item in filteredDoneItems" :key="`done-${item.id}`" class="mb-3 payment-card v-card v-sheet theme--light elevation-2">
              <div class="v-card__text">
                <div class="card-row"><span class="card-label">#:</span><span class="card-value">{{ item.id }}</span></div>
                <div class="card-row"><span class="card-label">{{ $t("finance.common.phone") }}:</span><span class="card-value">{{ item.phone }}</span></div>
                <div class="card-row"><span class="card-label">{{ $t("finance.common.email") }}:</span><span class="card-value">{{ item.email || '-' }}</span></div>
                <div class="card-row"><span class="card-label">{{ $t("finance.common.name") }}:</span><span class="card-value">{{ item.first_name }} {{ item.last_name }}</span></div>
                <div class="card-row"><span class="card-label">{{ $t("finance.common.type") }}:</span><span class="card-value">{{ item.type }}</span></div>
                <div class="card-row"><span class="card-label">{{ $t("finance.common.balance") }}:</span><span class="card-value balance positive">{{ formatBalance(item.balance) }}</span></div>
              </div>
              <div class="v-card__actions card-actions">
                <v-btn icon small @click="openLedger(item.id)"><v-icon>receipt_long</v-icon></v-btn>
                <v-btn icon small @click="goToCaptain(item.id)"><v-icon>account_circle</v-icon></v-btn>
              </div>
            </div>
          </div>
        </div>
      </v-tab-item>
    </v-tabs>
  </div>
</template>

<script>
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";

export default {
  name: "Payments",
  title() {
    return this.$t("finance.payments.pageTitle");
  },
  components: { FinanceSectionNav },
  data() {
    return {
      activeTab: 0,
      // final-version doc 2026-10-05: Vuetify 1.5 opens on the first entry,
      // which was 5 rows; open on 25 and keep "All".
      rowsPerPageItems: [25, 50, 100, { text: "$vuetify.dataIterator.rowsPerPageAll", value: -1 }],
      loading: {
        pending: false,
        done: false,
      },
      items: {
        pending: [],
        done: [],
      },
      search: {
        pending: "",
        done: "",
      },
      settlementDialog: false,
      settlementTarget: null,
      settlementForm: {
        amount: null,
      },
      settling: false,
    };
  },
  computed: {
    headers() {
      return [
        { text: "#", value: "id", align: "start" },
        { text: this.$t("finance.payments.firstName"), value: "first_name", align: "start" },
        { text: this.$t("finance.payments.lastName"), value: "last_name", align: "start" },
        { text: this.$t("finance.common.phone"), value: "phone", align: "start" },
        { text: this.$t("finance.common.email"), value: "email", align: "start" },
        { text: this.$t("finance.common.type"), value: "type", align: "start" },
        { text: this.$t("finance.common.balance"), value: "balance", align: "end" },
        { text: this.$t("finance.common.actions"), value: "actions", sortable: false, align: "center" },
      ];
    },
    filteredPendingItems() {
      return this.filterItems(this.items.pending, this.search.pending);
    },
    filteredDoneItems() {
      return this.filterItems(this.items.done, this.search.done);
    },
  },
  mounted() {
    this.loadBalances("pending");
    this.loadBalances("done");
  },
  methods: {
    getErrorMessage(error, fallback) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        return error.response.data.message;
      }

      return fallback;
    },
    filterItems(items, query) {
      const normalized = String(query || "").trim().toLowerCase();
      if (!normalized) {
        return items;
      }

      return items.filter((item) => {
        return [
          item.id,
          item.first_name,
          item.last_name,
          item.phone,
          item.email,
          item.type,
          item.balance,
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalized);
      });
    },
    loadBalances(status) {
      this.loading[status] = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_CAPTAIN_BALANCES, {
          params: {
            status,
            perPage: 100,
          },
        })
        .then(({ data }) => {
          this.items[status] = data.data.data || [];
        })
        .catch((error) => {
          const statusLabel = this.$t("finance.payments." + status);
          window.getApp.$emit(
            "SHOW_TOAST",
            this.getErrorMessage(error, this.$t("finance.payments.failedLoadStatus", { status: statusLabel })),
            "red"
          );
        })
        .finally(() => {
          this.loading[status] = false;
        });
    },
    formatBalance(value) {
      return this.formatMoney(value);
    },
    goToCaptain(id) {
      let routeData = this.$router.resolve(`/captains/${id}`);
      window.open(routeData.href, "_blank");
    },
    openLedger(id) {
      this.$router.push({
        name: "CaptainLedger",
        params: { captainId: String(id) },
      });
    },
    openSettlementDialog(item) {
      this.settlementTarget = item;
      // Prefill the input with a plain NEW-unit number (old balance / 100), not a
      // formatted string — submitSettlement multiplies it back by 100. Using
      // formatMoney() here would yield a grouped string ("1,234.56") that
      // toStoredAmount() parses as NaN -> null amount.
      this.settlementForm.amount = item.balance == null ? null : item.balance / 100;
      this.settlementDialog = true;
    },
    closeSettlementDialog() {
      this.settlementDialog = false;
      this.settlementTarget = null;
      this.settlementForm.amount = null;
    },
    submitSettlement(direction) {
      if (!this.settlementTarget) {
        return;
      }

      this.settling = true;
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_SETTLEMENTS, {
          captain_id: this.settlementTarget.id,
          amount: this.toStoredAmount(this.settlementForm.amount || 0),
          direction,
        })
        .then(() => {
          window.getApp.$emit("SHOW_TOAST", this.$t("finance.payments.settlementSaved"), "green");
          this.closeSettlementDialog();
          this.loadBalances("pending");
          this.loadBalances("done");
        })
        .catch((error) => {
          window.getApp.$emit(
            "SHOW_TOAST",
            this.getErrorMessage(error, this.$t("finance.payments.failedSaveSettlement")),
            "red"
          );
        })
        .finally(() => {
          this.settling = false;
        });
    },
  },
};
</script>

<style scoped>
.pay-header {
  padding-top: 8px;
}
.payment-card .card-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.payment-card .card-label {
  font-weight: 600;
}

.payment-card .balance.positive {
  color: #2e7d32;
}

.card-actions {
  justify-content: flex-end;
}
</style>